import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Tooltip, Collapse, Switch, InputNumber } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import SelectLn2Message from "../../../../components/select/SelectLn2Message";
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
import DataValueEditor from "./DataValueEditor";
import AttributeSelect from "./AttributeSelect";
// FLATBUFFER ENUMS
import { AttributeId } from "../../../../../flatbuffers/generated/opc-ua/attribute-id";
// INTERFACES
import type {
  OpcUaNodeIdForm,
  OpcUaNodeToMessageRow,
  OpcUaMessageToNodeRow,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";
// UTILS
import { describeUnbindableNode, opcUaNodeIdString } from "../../../../../utils/opcUaNodeId";
import { messageCarriesPosition } from "../../../../../flatbuffers/types/ln2MessageOptions";

const { Panel } = Collapse;

const BLANK_NODE: OpcUaNodeIdForm = BLANK_NUMERIC_NODE_ID;
const BLANK_VALUE = { kind: "string" as const, value: "" };

/** True when a NodeId form has a non-empty identifier (or, for a BrowsePath, at least one hop) — the minimum bar for a row to be addable. */
function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

/** Canonical description of a NodeId for display in the editor.
 *
 *  For every kind that has one, this is the real OPC UA `NodeId` string
 *  (`ns=2;s=Lift1.Position`) rather than a paraphrase of it, so what the
 *  operator reads here is what is actually sent. Kinds with no `NodeId`
 *  encoding fall back to a readable form.
 *
 *  This is a **display** helper. It is deliberately no longer the source of
 *  the bank binding's wire keys — those come from `opcUaNodeIdString`, whose
 *  spelling is a contract with LiftCloud's parser. Keeping one function for
 *  both jobs is what let the two sides disagree silently. */
export function describeNode(node: OpcUaNodeIdForm): string {
  return opcUaNodeIdString(node) ?? describeUnbindableNode(node);
}

/** Largest floor `position` can hold: it is a uint16 on the wire. */
export const MAX_FLOOR_POSITION = 65535;

/**
 * The floor a row should hold once `msgId` is its message.
 *
 * A message that carries a position (every elevator Traffic and Control
 * message) always has a floor on the wire: `position` is a uint16, so there is
 * no "no floor" value and a row without one ships as floor 0. Such a row
 * therefore keeps the floor already staged, or starts at 0 so the operator sees
 * the value that will be sent. Any other message has no `position` field, so its
 * row holds null and nothing is written.
 */
function positionForMessage(msgId: string, staged: number | null): number | null {
  if (!messageCarriesPosition(msgId)) return null;
  return staged ?? 0;
}

/** True when a row whose message carries a position is missing its floor. */
function isFloorMissing(row: { msgId: string; position: number | null }): boolean {
  return messageCarriesPosition(row.msgId) && row.position === null;
}

/**
 * The floor a mapping row addresses, shown only for a message that carries one
 * (see `positionForMessage`).
 *
 * The value is the LN2 floor index, counted from 0 at the lowest floor. VTS
 * forwards the mapped message unchanged and applies no vendor floor offset, so
 * the adapter's Vendor Floor Indexing setting does not apply here.
 *
 * The floor is required for these messages. Clearing the input leaves the row
 * without one, which blocks Add and shows why, rather than letting an empty box
 * stand for the floor 0 the builder would actually send.
 */
function PositionField({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (position: number | null) => void;
}) {
  const [t] = useTranslation("global");
  const missing = value === null;
  return (
    <div className="col-12 col-md-3 mt-2">
      <span className="generalStyles__inputLabel">
        {t("general.opcUaMappingFloor")}
        <Tooltip title={t("general.opcUaMappingFloorHelp")}>
          <FontAwesomeIcon icon={faCircleInfo} className="generalStyles__mlFix" />
        </Tooltip>
      </span>
      <InputNumber
        data-doc-label="Floor (position)"
        className="generalStyles__width100"
        min={0}
        max={MAX_FLOOR_POSITION}
        precision={0}
        status={missing ? "error" : undefined}
        value={value}
        onChange={(next) => onChange(typeof next === "number" ? next : null)}
      />
      {missing && (
        <div role="alert" style={{ color: "#ff4d4f" }}>
          {t("general.opcUaMappingFloorRequired")}
        </div>
      )}
    </div>
  );
}

export default function MappingsEditor({
  nodeToMsg,
  msgToNode,
  onChangeNodeToMsg,
  onChangeMsgToNode,
}: {
  nodeToMsg: OpcUaNodeToMessageRow[];
  msgToNode: OpcUaMessageToNodeRow[];
  onChangeNodeToMsg: (rows: OpcUaNodeToMessageRow[]) => void;
  onChangeMsgToNode: (rows: OpcUaMessageToNodeRow[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draftNodeToMsg, setDraftNodeToMsg] = useState<OpcUaNodeToMessageRow>({
    node: { ...BLANK_NODE },
    nodeValue: { ...BLANK_VALUE },
    attributeId: AttributeId.Value,
    msgId: "",
    active: true,
    position: null,
  });
  const [draftMsgToNode, setDraftMsgToNode] = useState<OpcUaMessageToNodeRow>({
    msgId: "",
    active: true,
    node: { ...BLANK_NODE },
    nodeValue: { ...BLANK_VALUE },
    attributeId: AttributeId.Value,
    position: null,
  });

  const canAddNodeToMsg =
    isNodeComplete(draftNodeToMsg.node) &&
    draftNodeToMsg.msgId !== "" &&
    !isFloorMissing(draftNodeToMsg);
  const canAddMsgToNode =
    isNodeComplete(draftMsgToNode.node) &&
    draftMsgToNode.msgId !== "" &&
    !isFloorMissing(draftMsgToNode);

  const onAddNodeToMsg = () => {
    if (!canAddNodeToMsg) return;
    onChangeNodeToMsg([...nodeToMsg, draftNodeToMsg]);
    setDraftNodeToMsg({
      node: { ...BLANK_NODE },
      nodeValue: { ...BLANK_VALUE },
      attributeId: AttributeId.Value,
      msgId: "",
      active: true,
      position: null,
    });
  };

  const onAddMsgToNode = () => {
    if (!canAddMsgToNode) return;
    onChangeMsgToNode([...msgToNode, draftMsgToNode]);
    setDraftMsgToNode({
      msgId: "",
      active: true,
      node: { ...BLANK_NODE },
      nodeValue: { ...BLANK_VALUE },
      attributeId: AttributeId.Value,
      position: null,
    });
  };

  const onRemoveNodeToMsg = (index: number) => {
    onChangeNodeToMsg(nodeToMsg.filter((_, i) => i !== index));
  };

  const onRemoveMsgToNode = (index: number) => {
    onChangeMsgToNode(msgToNode.filter((_, i) => i !== index));
  };

  return (
    <div className="container mt-3">
      {/* NODE -> MESSAGE (inbound: node reaches a value -> emit LN2 message) */}
      <Collapse defaultActiveKey={["node-to-msg"]}>
        <Panel
          header={t("general.opcUaNodeToMessageMappings", { total: nodeToMsg.length })}
          key="node-to-msg"
        >
          {nodeToMsg.map((row, index) => (
            <div
              key={`${row.node.namespaceIndex}-${row.node.identifier}-${row.msgId}-${index}`}
              className="mb-3 generalStyles__flex"
              style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
            >
              <div style={{ width: "100%" }}>
                <div className="generalStyles__textEllipsis">
                  {describeNode(row.node)} (value=&quot;{row.nodeValue.value}&quot;) → {row.msgId} (
                  {row.active ? "active" : "inactive"})
                  {row.position !== null ? ` floor ${row.position}` : ""}
                </div>
              </div>
              <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                <Tooltip title="Delete">
                  <Button
                    onClick={() => onRemoveNodeToMsg(index)}
                    className="buttonStyle__29"
                    shape="circle"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
          <NodeIdEditor
            node={draftNodeToMsg.node}
            onChangeNode={(node) => setDraftNodeToMsg({ ...draftNodeToMsg, node })}
          />
          <div className="row mt-2">
            <div className="col-12 col-md-6">
              <AttributeSelect
                value={draftNodeToMsg.attributeId}
                onChange={(attributeId) => setDraftNodeToMsg({ ...draftNodeToMsg, attributeId })}
              />
            </div>
          </div>
          <DataValueEditor
            value={draftNodeToMsg.nodeValue}
            onChange={(nodeValue) => setDraftNodeToMsg({ ...draftNodeToMsg, nodeValue })}
          />
          <div className="row">
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">LN2 Message</span>
              <SelectLn2Message
                value={draftNodeToMsg.msgId}
                onChange={(key) =>
                  setDraftNodeToMsg({
                    ...draftNodeToMsg,
                    msgId: key,
                    // Default a floor-carrying message to floor 0, and drop a
                    // staged floor when the new message cannot carry one.
                    position: positionForMessage(key, draftNodeToMsg.position),
                  })
                }
              />
            </div>
            {messageCarriesPosition(draftNodeToMsg.msgId) && (
              <PositionField
                value={draftNodeToMsg.position}
                onChange={(position) => setDraftNodeToMsg({ ...draftNodeToMsg, position })}
              />
            )}
            <div className="col-12 col-md-3 mt-2">
              <div className="generalStyles__flex">
                <span className="generalStyles__inputLabel">Active</span>
                <Switch
                  checked={draftNodeToMsg.active}
                  onChange={(checked) => setDraftNodeToMsg({ ...draftNodeToMsg, active: checked })}
                  style={{ marginLeft: 8 }}
                />
              </div>
            </div>
            <div className="col-12 col-md-3 mt-2">
              <Button
                onClick={onAddNodeToMsg}
                disabled={!canAddNodeToMsg}
                className={`generalStyles__width100 ${canAddNodeToMsg ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
      <br />
      {/* MESSAGE -> NODE (outbound: LN2 command -> write a value to a node) */}
      <Collapse>
        <Panel
          header={t("general.opcUaMessageToNodeMappings", { total: msgToNode.length })}
          key="msg-to-node"
        >
          {msgToNode.map((row, index) => (
            <div
              key={`${row.msgId}-${row.node.namespaceIndex}-${row.node.identifier}-${index}`}
              className="mb-3 generalStyles__flex"
              style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
            >
              <div style={{ width: "100%" }}>
                <div className="generalStyles__textEllipsis">
                  {row.msgId} ({row.active ? "active" : "inactive"}) → {describeNode(row.node)}{" "}
                  (value=&quot;{row.nodeValue.value}&quot;)
                  {row.position !== null ? ` floor ${row.position}` : ""}
                </div>
              </div>
              <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                <Tooltip title="Delete">
                  <Button
                    onClick={() => onRemoveMsgToNode(index)}
                    className="buttonStyle__29"
                    shape="circle"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
          <div className="row">
            <div className="col-12 col-md-6 mt-2">
              <span className="generalStyles__inputLabel">LN2 Message</span>
              <SelectLn2Message
                value={draftMsgToNode.msgId}
                onChange={(key) =>
                  setDraftMsgToNode({
                    ...draftMsgToNode,
                    msgId: key,
                    position: positionForMessage(key, draftMsgToNode.position),
                  })
                }
              />
            </div>
            {messageCarriesPosition(draftMsgToNode.msgId) && (
              <PositionField
                value={draftMsgToNode.position}
                onChange={(position) => setDraftMsgToNode({ ...draftMsgToNode, position })}
              />
            )}
            <div className="col-12 col-md-3 mt-2">
              <div className="generalStyles__flex">
                <span className="generalStyles__inputLabel">Active</span>
                <Switch
                  checked={draftMsgToNode.active}
                  onChange={(checked) => setDraftMsgToNode({ ...draftMsgToNode, active: checked })}
                  style={{ marginLeft: 8 }}
                />
              </div>
            </div>
          </div>
          <NodeIdEditor
            node={draftMsgToNode.node}
            onChangeNode={(node) => setDraftMsgToNode({ ...draftMsgToNode, node })}
          />
          <div className="row mt-2">
            <div className="col-12 col-md-6">
              <AttributeSelect
                value={draftMsgToNode.attributeId}
                onChange={(attributeId) => setDraftMsgToNode({ ...draftMsgToNode, attributeId })}
              />
            </div>
          </div>
          <DataValueEditor
            value={draftMsgToNode.nodeValue}
            onChange={(nodeValue) => setDraftMsgToNode({ ...draftMsgToNode, nodeValue })}
          />
          <div className="row mt-2">
            <div className="col-12 col-md-3">
              <Button
                onClick={onAddMsgToNode}
                disabled={!canAddMsgToNode}
                className={`generalStyles__width100 ${canAddMsgToNode ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
